import { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Card } from "primereact/card";
import { FiSearch, FiX } from "react-icons/fi";
import useGetPhysicalBooks from "api/BookResource/useGetPhysicalBooks";
import useFetchCategories from "api/CategoryResource/useFetchCategories";
import useGenres from "api/GenreResource/useGenres";
import { CustomPaginator } from "components";
import usePhysicalBooksFilters from "./usePhysicalBooksFilters";
import Admin2 from "layouts/Admin2";
import UpdatePhysicalBook from "./UpdatePhysicalBook";
import AddPhysicalBook from "./AddPhysicalBook";
import BookCoverPreview from "components/BookCoverPreview";

const AllPhysicalBooks = () => {
	const filters = usePhysicalBooksFilters();
	const [expandedRows, setExpandedRows] = useState(null);
	const [searchInput, setSearchInput] = useState(filters.searchTerm);

	// Sync search input with URL parameter
	useEffect(() => {
		setSearchInput(filters.searchTerm);
	}, [filters.searchTerm]);

	// Fetch data
	const { data: books, isLoading } = useGetPhysicalBooks({
		page: filters.page,
		rows_per_page: filters.rowsPerPage,
		searchTerm: filters.searchTerm,
		"filter[category_is]": filters.categoryIs,
		"filter[genre_is]": filters.genreIs,
	});

	const { data: categories } = useFetchCategories("yes");
	const { data: genres } = useGenres("yes");

	const booksList = books?.data?.records || [];
	const totalRecords = books?.data?.total_records || 0;

	// Book cover template
	const bookCoverTemplate = (rowData) => {
		return (
			<BookCoverPreview
				coverUrl={rowData?.book_cover?.url}
				title={rowData?.title}
				thumbnailWidth={64}
				thumbnailHeight={80}
			/>
		);
	};

	// Price template
	const priceTemplate = (rowData) => {
		return (
			<div className="flex flex-col gap-1">
				<div className="text-sm">
					<span className="font-semibold">USD:</span> $
					{rowData?.physical_usd_price || 0}
				</div>
				<div className="text-sm">
					<span className="font-semibold">ZWL:</span> $
					{rowData?.physical_zwl_price || 0}
				</div>
			</div>
		);
	};

	// Stock level template
	const stockTemplate = (rowData) => {
		const stockLevel = rowData?.stock_level || 0;
		const severity =
			stockLevel > 10 ? "success" : stockLevel > 0 ? "warning" : "danger";
		const value =
			stockLevel > 10
				? "In Stock"
				: stockLevel > 0
				? "Low Stock"
				: "Out of Stock";

		return (
			<Tag
				value={value}
				severity={severity}
				className="text-xs"
			/>
		);
	};

	// Stock level number template
	const stockLevelTemplate = (rowData) => {
		const stockLevel = rowData?.stock_level || 0;
		return <span className="font-semibold text-gray-700">{stockLevel}</span>;
	};

	// Category template
	const categoryTemplate = (rowData) => {
		return (
			<span className="text-sm text-gray-600">
				{rowData?.category?.name || "N/A"}
			</span>
		);
	};

	// Row expansion template
	const rowExpansionTemplate = (data) => {
		return (
			<div className="p-4 bg-gray-50 border-t border-gray-200">
				<Card className="shadow-none border-0">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						<div>
							<h4 className="font-semibold text-lg mb-3">Book Details</h4>
							<div className="space-y-2 text-sm">
								<div>
									<span className="font-medium">ISBN:</span>{" "}
									{data.isbn || "N/A"}
								</div>
							<div>
								<span className="font-medium">Publisher:</span>{" "}
								{data.publisher || "N/A"}
							</div>
								<div>
									<span className="font-medium">Format:</span>{" "}
									{data.format || "Physical"}
								</div>
								<div>
									<span className="font-medium">Status:</span>
									<Tag
										value={data.is_active ? "Active" : "Inactive"}
										severity={data.is_active ? "success" : "danger"}
										className="ml-2"
									/>
								</div>
							</div>
						</div>
						<div>
							<h4 className="font-semibold text-lg mb-3">Description</h4>
							<div
								className="text-sm text-gray-700 prose prose-sm max-w-none"
								dangerouslySetInnerHTML={{
									__html: data.description || "No description available",
								}}
							/>
						</div>
					</div>
				</Card>
			</div>
		);
	};

	// Actions template
	const actionsTemplate = (rowData) => {
		return <UpdatePhysicalBook book={rowData} />;
	};

	// Search and filter handlers
	const handleSearchInputChange = (e) => {
		setSearchInput(e.target.value);
	};

	const handleSearchSubmit = (e) => {
		e.preventDefault();
		filters.changeFilter("searchTerm", searchInput);
	};

	const handleSearch = () => {
		filters.changeFilter("searchTerm", searchInput);
	};

	const handleKeyPress = (e) => {
		if (e.key === "Enter") {
			e.preventDefault();
			handleSearch();
		}
	};

	const handleCategoryChange = (e) => {
		filters.changeFilter("categoryIs", e.value);
	};

	const handleGenreChange = (e) => {
		filters.changeFilter("genreIs", e.value);
	};

	const clearFilters = () => {
		setSearchInput("");
		filters.changeFilter("searchTerm", "");
		filters.changeFilter("categoryIs", "");
		filters.changeFilter("genreIs", "");
	};

	// Count active filters
	const activeFiltersCount = [
		filters.searchTerm,
		filters.categoryIs,
		filters.genreIs,
	].filter(Boolean).length;

	// Category options
	const categoryOptions =
		categories?.data?.map((cat) => ({
			label: cat.name,
			value: cat.id,
		})) || [];

	// Genre options
	const genreOptions =
		genres?.data?.data?.map((genre) => ({
			label: genre.name,
			value: genre.id,
		})) || [];

	return (
		<Admin2 fullWidth>
			<div className=" w-full">
				{/* Header */}
				<div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
					<div className="flex items-center gap-3">
						<h1 className="text-2xl font-bold ">Physical Books</h1>
						<span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
							{totalRecords} books
						</span>
					</div>
					<div className="flex items-center gap-2">
						<AddPhysicalBook />
					</div>
				</div>

				{/* Search and Filters */}
				<div className="shadow-sm pt-4">
					<div className="space-y-3">
						{/* Search and Filters Row */}
						<div className="flex flex-col lg:flex-row gap-3 items-start lg:items-end">
							{/* Search Bar */}
							<div className="flex-1 min-w-0">
								<form
									onSubmit={handleSearchSubmit}
									className="relative"
								>
									<FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
									<InputText
										value={searchInput}
										onChange={handleSearchInputChange}
										onKeyDown={handleKeyPress}
										placeholder="Search books by title, author, ISBN..."
										className="w-full pl-10 pr-20 border rounded-lg"
									/>
									<Button
										type="submit"
										label="Search"
										className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 px-3 text-sm rounded-lg"
									/>
								</form>
							</div>

							{/* Filters */}
							<div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
								<div className="flex-1 sm:flex-none sm:w-44">
									<Dropdown
										value={filters.categoryIs}
										onChange={handleCategoryChange}
										options={categoryOptions}
										placeholder="Category"
										className="w-full"
										showClear
									/>
								</div>
								<div className="flex-1 sm:flex-none sm:w-44">
									<Dropdown
										value={filters.genreIs}
										onChange={handleGenreChange}
										options={genreOptions}
										placeholder="Genre"
										className="w-full"
										showClear
									/>
								</div>
								{activeFiltersCount > 0 && (
									<Button
										label={`Clear (${activeFiltersCount})`}
										icon={<FiX />}
										onClick={clearFilters}
										className="p-button-outlined p-button-secondary h-10"
									/>
								)}
							</div>
						</div>

						{/* Active Filters Display */}
						{activeFiltersCount > 0 && (
							<div className="flex flex-wrap gap-2 pt-2 border-gray-100">
								{filters.searchTerm && (
									<Tag
										value={`Search: ${filters.searchTerm}`}
										severity="info"
										className="text-xs"
									/>
								)}
								{filters.categoryIs && (
									<Tag
										value={`Category: ${
											categoryOptions.find(
												(cat) => cat.value === parseInt(filters.categoryIs)
											)?.label || "Unknown Category"
										}`}
										severity="info"
										className="text-xs"
									/>
								)}
								{filters.genreIs && (
									<Tag
										value={`Genre: ${
											genreOptions.find(
												(genre) => genre.value === parseInt(filters.genreIs)
											)?.label || "Unknown Genre"
										}`}
										severity="info"
										className="text-xs"
									/>
								)}
							</div>
						)}
					</div>
				</div>

				{/* DataTable */}
				<div className="shadow-sm pt-4">
					<DataTable
						value={booksList}
						expandedRows={expandedRows}
						onRowToggle={(e) => setExpandedRows(e.data)}
						rowExpansionTemplate={rowExpansionTemplate}
						dataKey="id"
						tableClassName="font-sans text-sm"
						loading={isLoading}
						emptyMessage="No physical books found"
						autoLayout
					>
						<Column
							expander
							style={{ width: "3rem" }}
						/>
						<Column
							header="Cover"
							body={bookCoverTemplate}
							style={{ width: "80px" }}
							className="text-center"
						/>
						<Column
							field="title"
							header="Title"
							style={{ minWidth: "200px" }}
						/>
						<Column
							field="author"
							header="Author"
						/>
						<Column
							header="Category"
							body={categoryTemplate}
							style={{ width: "200px" }}
						/>
						<Column
							header="Price"
							body={priceTemplate}
							style={{ width: "120px" }}
						/>
						<Column
							header="Stock Level"
							body={stockLevelTemplate}
							style={{ width: "100px" }}
							className="text-center"
						/>
						<Column
							header="Stock Status"
							body={stockTemplate}
							style={{ width: "120px" }}
							className="text-center"
						/>
						<Column
							header="Actions"
							body={actionsTemplate}
							style={{ width: "120px" }}
							className="text-center"
						/>
					</DataTable>

					{/* Pagination */}
					<div className="flex justify-center mt-6">
						<CustomPaginator
							firstPage={filters.firstPage}
							rowsPerPage={filters.rowsPerPage}
							setFirstPage={(val) => filters.changeFilter("firstPage", val)}
							setRowsPerPage={(val) => filters.changeFilter("rowsPerPage", val)}
							totalRecords={totalRecords}
							setPage={(val) => filters.changeFilter("page", val)}
							showRowsPerPageDropdown={true}
						/>
					</div>
				</div>
			</div>
		</Admin2>
	);
};

export default AllPhysicalBooks;
